import { useState } from "react";
import { Button, Icon, Modal, Select } from "@legal-500/legal500-design-system";
import type { SelectOption } from "@legal-500/legal500-design-system";
import { templates, type MailingTemplate, type TemplateType } from "@/api/templates.service";
import styles from "./MailingTemplates.module.css";

type EditTypeModalProps = {
  template: MailingTemplate;
  onClose: () => void;
  /**
   * Called after a successful save with the updated config fields so the parent
   * can patch the affected row (and trigger a refetch for server-side filters).
   */
  onSaved: (next: {
    type: TemplateType | null;
    archived: boolean;
    status: MailingTemplate["status"];
  }) => void;
};

// The empty value clears the assignment (type → null).
const TYPE_OPTIONS: SelectOption[] = [
  { value: "", label: "— (unassigned)" },
  { value: "survey_request", label: "Survey request" },
  { value: "reminder", label: "Reminder" },
];

/**
 * Modal for assigning (or clearing) a template's type. The template name is
 * shown read-only for context; the Type dropdown is pre-selected to the current
 * value and Save calls `setConfig` (allowing `type: null` to unassign).
 */
export function EditTypeModal({ template, onClose, onSaved }: EditTypeModalProps) {
  const [type, setType] = useState<TemplateType | "">(template.type ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    const nextType = (type || null) as TemplateType | null;
    try {
      const resp = await templates.setConfig(template.sendgrid_template_id, { type: nextType });
      onSaved({
        type: resp.template.type,
        archived: resp.template.archived,
        status: resp.template.status,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update template type");
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Edit template type" size="medium" blurBackground>
      <div className={styles.form}>
        <div className={styles.editTemplateName}>
          <span className={styles.editTemplateNameLabel}>Template</span>
          <span>{template.name ?? template.sendgrid_template_id}</span>
        </div>

        <Select
          label="Type"
          options={TYPE_OPTIONS}
          value={type}
          onChange={e => setType(e.target.value as TemplateType | "")}
          disabled={saving}
          fullWidth
        />

        {error && (
          <div className={styles.formError}>
            <Icon icon="exclamation" size="small" />
            <span>{error}</span>
          </div>
        )}

        <div className={styles.modalFooter}>
          <Button variant="outlined" color="default" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
